import { Component, ContentChild, effect, HostListener, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { DepotFacade } from '@zero/depot/domain';
import { ChildOnboardingStepService, ChildOnboardingStore } from '@zero/onboarding/domain';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { combineLatest, filter, map } from 'rxjs';

@Component({
  template: '',
  host: { 'child-onboarding': '' },
})
export abstract class BaseComponent implements OnInit {
  @ContentChild(ButtonComponent) public btn?: ButtonComponent;

  public readonly store = inject(ChildOnboardingStore);
  public readonly service = inject(ChildOnboardingStepService);
  public readonly customerStateFacade = inject(CustomerStateFacade);
  public readonly depotFacade = inject(DepotFacade);

  readonly #route = inject(ActivatedRoute);

  public readonly routeId = toSignal(
    this.#route.queryParams.pipe(
      filter((params) => !!params),
      map((params) => params['id'] ?? null),
    ),
  );

  @HostListener('window:keydown', ['$event'])
  public pressEnter(event: KeyboardEvent): void {
    event.key === 'Enter' && this.submit();
  }

  constructor() {
    effect(() => {
      const id = this.routeId();
      const hasSelectedSituation = !!this.store.selectedSituation();

      if (!id && !hasSelectedSituation) {
        this.service.routeToHomeOnInvalidData();
        return;
      }

      const selectedId = this.store.selectedId();
      const isLoaded = this.store.childOnboardingsLoaded();
      const isLoading = this.store.childOnboardingsLoading();
      if (id && id !== selectedId && isLoaded && !isLoading) {
        this.store.setSelected(id).then(() => this.service.routeToHomeOnInvalidData());
      }
    });

    effect(() => {
      const selectedId = this.store.selectedId();
      const isLoaded = this.store.childOnboardingsLoaded();
      const isLoading = this.store.childOnboardingsLoading();
      if (!isLoading && isLoaded && selectedId) {
        this.service.initStepResetSuccess();
      }
    });
  }

  ngOnInit(): void {
    this.service.initialize();
    this.store.loadChildOnboardings();
    this.store.loadChildOnboardingSituations();
    this.store.loadCountries();

    this.#listenToOnboardingDisabled();
    this.populateFromData();
    this.listenToSuccessfulSubmit();
  }

  protected abstract submit(): Promise<void> | void;
  protected abstract populateFromData(): void;
  protected abstract listenToSuccessfulSubmit(): void;

  #listenToOnboardingDisabled(): void {
    combineLatest({
      isLoaded: this.depotFacade.loaded$,
      isUnderageOnboardingEnabled: this.depotFacade.underageOnboardingEnabled$,
    })
      .pipe(filter(({ isLoaded, isUnderageOnboardingEnabled }) => isLoaded && isUnderageOnboardingEnabled === false))
      .subscribe(() => this.service.redirectToHome());
  }
}
